<template>
  <button
    type="button"
    :class="computedClasses"
    :disabled="isDisabled"
   
    class="@container"
    @click="handleDateClick"
  >
    <time
      v-if="isCurrentMonth"
      :datetime="date.format('YYYY-MM-DD')"
      class="text-[max(25cqw,var(--text-sm))] @max-[33px]:text-sm"
    >
      {{ date.date() }}
    </time>
  </button>
</template>

<script setup lang="ts">
import type { Dayjs } from 'dayjs'

const props = defineProps<{
  date: Dayjs
  isCurrentMonth: boolean
  isSelected: boolean
  isSelectedRange: boolean
  isSelectedRangeStart: boolean
  isSelectedRangeEnd: boolean
  isDisabled?: boolean
  isUnavailable?: boolean
}>()

const emit = defineEmits<{
  'date-click': []
}>()

const { formatAsDateISO } = useDates()

const computedDayStateBgClasses = computed(() => {
  if (!props.isCurrentMonth) {
    return ''
  }

  if (props.isSelected) {
    return 'bg-primary-950 rounded-full'
  }

  if (props.isSelectedRange) {
    return 'bg-primary-200'
  }

  if (props.isDisabled) {
    return ''
  }

  if (props.isUnavailable) {
    return 'bg-white'
  }

  return 'bg-white rounded-full hover:bg-gray-100'
})

const computedDayStateTextClasses = computed(() => {
  if (props.isDisabled) {
    return 'text-gray-400 line-through'
  }

  if (props.isUnavailable) {
    return 'text-gray-400'
  }

  if (props.isSelected) {
    return 'font-semibold text-white'
  }

  if (props.isSelectedRange) {
    return 'font-semibold'
  }

  return ''
})

const computedDayPseudoClasses = computed(() => {
  if (!props.isSelected || !props.isSelectedRange) {
    return ''
  }

  const pseudoClasses = 'before:-z-1 before:content-[""] before:absolute before:top-0 before:w-[50%] before:h-full before:bg-primary-200'

  if (props.isSelectedRangeStart) {
    return `${pseudoClasses} before:right-0`
  }

  if (props.isSelectedRangeEnd) {
    return `${pseudoClasses} before:left-0`
  }

  return ''
})

const computedClasses = computed(() => {
  return [
    'flex items-center justify-center relative h-8 min-w-8 py-[50%]',
    computedDayStateBgClasses.value,
    computedDayStateTextClasses.value,
    computedDayPseudoClasses.value,
    {
      'cursor-pointer': !props.isDisabled && !props.isUnavailable && props.isCurrentMonth,
    },
  ]
})

const computedDataTestId = computed(() => {
  if (!props.isCurrentMonth) {
    return undefined
  }

  return `picker-${formatAsDateISO(props.date)}`
})

function handleDateClick() {
  if (!props.isCurrentMonth || props.isDisabled || props.isUnavailable) {
    return
  }

  emit('date-click')
}
</script>
